// India identifiers, checked in the browser for quick feedback (the database checks them again).
const CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const GSTIN_RE = /^[0-9]{2}[0-9A-Z]{10}[0-9A-Z]Z[0-9A-Z]$/;

export function gstinCheckChar(first14: string): string {
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const p = CHARS.indexOf(first14[i]) * (i % 2 ? 2 : 1);
    sum += Math.floor(p / 36) + (p % 36);
  }
  return CHARS[(36 - (sum % 36)) % 36];
}

export function gstinValid(g: string): boolean {
  return GSTIN_RE.test(g) && gstinCheckChar(g.slice(0, 14)) === g[14];
}

// what a GSTIN tells us: the state and (for most) the PAN
export function fromGstin(g: string): { state: string; pan: string | null } | null {
  if (!gstinValid(g)) return null;
  const pan = g.slice(2, 12);
  return { state: g.slice(0, 2), pan: PAN_RE.test(pan) ? pan : null };
}

export const panValid = (p: string) => PAN_RE.test(p);

// an amount in words the Indian way: "Rupees One Lakh Six Thousand Two Hundred and Fifty Paise Only"
const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen",
  "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
function under100(n: number) { return n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? " " + ONES[n % 10] : ""}`; }
function under1000(n: number) {
  const h = Math.floor(n / 100), r = n % 100;
  return [h ? `${ONES[h]} Hundred` : "", r ? under100(r) : ""].filter(Boolean).join(" ");
}
export function indianWords(n: number): string {
  if (n === 0) return "Zero";
  const parts: string[] = [];
  const crore = Math.floor(n / 1e7); n %= 1e7;
  const lakh = Math.floor(n / 1e5); n %= 1e5;
  const thousand = Math.floor(n / 1e3); n %= 1e3;
  if (crore) parts.push(`${indianWords(crore)} Crore`);
  if (lakh) parts.push(`${under100(lakh)} Lakh`);
  if (thousand) parts.push(`${under100(thousand)} Thousand`);
  if (n) parts.push(under1000(n));
  return parts.join(" ");
}
export function rupeesInWords(amount: number): string {
  const a = Math.round(Math.abs(amount) * 100);
  const r = Math.floor(a / 100), p = a % 100;
  return `Rupees ${indianWords(r)}${p ? ` and ${under100(p)} Paise` : ""} Only`;
}

// an amount in a foreign currency, in international words: "US Dollars One Thousand and Twenty Five Cents Only"
const UNITS: Record<string, [string, string]> = {
  USD: ["US Dollars", "Cents"], EUR: ["Euros", "Cents"], GBP: ["Pounds Sterling", "Pence"], AED: ["UAE Dirhams", "Fils"],
  SGD: ["Singapore Dollars", "Cents"], AUD: ["Australian Dollars", "Cents"], CAD: ["Canadian Dollars", "Cents"], SAR: ["Saudi Riyals", "Halalas"],
  JPY: ["Japanese Yen", ""], CHF: ["Swiss Francs", "Centimes"],
};
export function internationalWords(n: number): string {
  if (n === 0) return "Zero";
  const parts: string[] = [];
  for (const [size, name] of [[1e12, "Trillion"], [1e9, "Billion"], [1e6, "Million"], [1e3, "Thousand"]] as const) {
    const k = Math.floor(n / size); n %= size;
    if (k) parts.push(`${under1000(k)} ${name}`);
  }
  if (n) parts.push(under1000(n));
  return parts.join(" ");
}
export function amountInWords(amount: number, currency = "INR"): string {
  if (currency === "INR") return rupeesInWords(amount);
  const [major, minor] = UNITS[currency] ?? [currency, "Hundredths"];
  const a = Math.round(Math.abs(amount) * 100);
  const m = Math.floor(a / 100), p = a % 100;
  return `${major} ${internationalWords(m)}${p && minor ? ` and ${under100(p)} ${minor}` : ""} Only`;
}

// GST state codes, for forms outside a company (Altekore's own billing)
export const STATES: [string, string][] = [["01", "Jammu and Kashmir"], ["02", "Himachal Pradesh"], ["03", "Punjab"], ["04", "Chandigarh"], ["05", "Uttarakhand"],
  ["06", "Haryana"], ["07", "Delhi"], ["08", "Rajasthan"], ["09", "Uttar Pradesh"], ["10", "Bihar"], ["11", "Sikkim"], ["12", "Arunachal Pradesh"], ["13", "Nagaland"],
  ["14", "Manipur"], ["15", "Mizoram"], ["16", "Tripura"], ["17", "Meghalaya"], ["18", "Assam"], ["19", "West Bengal"], ["20", "Jharkhand"], ["21", "Odisha"],
  ["22", "Chhattisgarh"], ["23", "Madhya Pradesh"], ["24", "Gujarat"], ["26", "Dadra and Nagar Haveli and Daman and Diu"], ["27", "Maharashtra"], ["29", "Karnataka"],
  ["30", "Goa"], ["31", "Lakshadweep"], ["32", "Kerala"], ["33", "Tamil Nadu"], ["34", "Puducherry"], ["35", "Andaman and Nicobar Islands"], ["36", "Telangana"],
  ["37", "Andhra Pradesh"], ["38", "Ladakh"]];
